import { useState, useEffect, useRef } from 'react';
import type { HITLRendererProps } from '../HITLRenderer';
import { hitlStyles, cn } from './styles';
import {
  initOAuthConnection,
  openOAuthWindow,
  pollForOAuthConnection,
  OAUTH_POLL_INTERVAL_MS,
} from '@/lib/oauth';

const PROVIDER_CONFIG = {
  hubspot: {
    name: 'HubSpot',
    icon: '🟠',
    color: 'bg-orange-50 border-orange-200',
    buttonColor: 'bg-orange-500 hover:bg-orange-600',
  },
  salesforce: {
    name: 'Salesforce',
    icon: '☁️',
    color: 'bg-domino-lilac border-domino-cardBorder',
    buttonColor: 'bg-domino-aubergine hover:bg-domino-prune',
  },
  google: {
    name: 'Google',
    icon: '🔵',
    color: 'bg-red-50 border-red-200',
    buttonColor: 'bg-red-500 hover:bg-red-600',
  },
  google_calendar: {
    name: 'Google Calendar',
    icon: '📅',
    color: 'bg-domino-lilac border-domino-cardBorder',
    buttonColor: 'bg-domino-aubergine hover:bg-domino-prune',
  },
  google_workspace: {
    name: 'Google Workspace',
    icon: '📁',
    color: 'bg-domino-lilac border-domino-cardBorder',
    buttonColor: 'bg-domino-aubergine hover:bg-domino-prune',
  },
  gmail: {
    name: 'Gmail',
    icon: '✉️',
    color: 'bg-rose-50 border-rose-200',
    buttonColor: 'bg-rose-500 hover:bg-rose-600',
  },
};

export const OAuthRenderer: React.FC<HITLRendererProps> = ({
  response,
  onSubmit,
  onCancel,
  isSubmitting,
  compact,
}) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isWaitingForOAuth, setIsWaitingForOAuth] = useState(false);
  const [pollAttempts, setPollAttempts] = useState(0);
  const [oauthError, setOAuthError] = useState<string | null>(null);
  const pollAbortRef = useRef<AbortController | null>(null);

  const provider = response.provider || 'hubspot';
  const config = PROVIDER_CONFIG[provider as keyof typeof PROVIDER_CONFIG] || PROVIDER_CONFIG.hubspot;

  useEffect(() => {
    return () => {
      pollAbortRef.current?.abort();
      pollAbortRef.current = null;
    };
  }, []);

  const handleConnect = async () => {
    setIsConnecting(true);
    setOAuthError(null);
    try {
      const { url, attemptId } = await initOAuthConnection(provider);
      const windowResult = openOAuthWindow(url);
      if (!windowResult.shouldPoll) {
        setIsConnecting(false);
        return;
      }

      pollAbortRef.current?.abort();
      const abortController = new AbortController();
      pollAbortRef.current = abortController;

      setIsWaitingForOAuth(true);
      setPollAttempts(0);

      const pollResult = await pollForOAuthConnection({
        provider,
        attemptId,
        signal: abortController.signal,
        onTick: ({ attempts }) => {
          setPollAttempts(attempts);
        },
      });

      if (pollAbortRef.current === abortController) {
        pollAbortRef.current = null;
      }

      if (pollResult.aborted) {
        return;
      }

      if (pollResult.connected) {
        setIsWaitingForOAuth(false);
        setIsConnecting(false);
        onSubmit('connect_now');
        return;
      }

      if (!pollResult.aborted && pollResult.timedOut) {
        setOAuthError('Autorisation non terminée à temps. Réessayez la connexion.');
      }

      setIsWaitingForOAuth(false);
      setIsConnecting(false);
    } catch (error) {
      console.error('OAuth failed:', error);
      setOAuthError(error instanceof Error ? error.message : 'Connexion OAuth impossible');
      setIsWaitingForOAuth(false);
      setIsConnecting(false);
    }
  };

  const handleCancel = () => {
    pollAbortRef.current?.abort();
    pollAbortRef.current = null;
    setIsWaitingForOAuth(false);
    setIsConnecting(false);
    setPollAttempts(0);

    if (onCancel) {
      onCancel();
    } else {
      onSubmit('cancel');
    }
  };

  const getButtonText = () => {
    if (isWaitingForOAuth) return `En attente de ${config.name}...`;
    if (isConnecting || isSubmitting) return 'Connexion...';
    return `Connecter ${config.name}`;
  };

  return (
    <div className={cn(
      compact ? hitlStyles.containerCompact : hitlStyles.container,
      config.color
    )}>
      <div className="flex items-center gap-3 mb-3">
        <span className="text-2xl">{config.icon}</span>
        <div>
          <h3 className="text-sm font-semibold text-gray-900">
            Connecter {config.name}
          </h3>
          <p className="text-xs text-gray-600">
            {isWaitingForOAuth
              ? `Complétez l'autorisation dans la fenêtre popup.`
              : (response.message || `L'intégration ${config.name} est requise pour continuer.`)
            }
          </p>
        </div>
      </div>

      {response.context && !isWaitingForOAuth && (
        <p className="text-xs text-gray-500 mb-3">{response.context}</p>
      )}

      {isWaitingForOAuth && (
        <div className="text-xs text-gray-500 mb-3 flex items-center gap-2">
          <span className={hitlStyles.spinner} />
          <span>Vérification... ({Math.floor(pollAttempts * OAUTH_POLL_INTERVAL_MS / 1000)}s)</span>
        </div>
      )}

      {oauthError && !isWaitingForOAuth && (
        <p className="text-xs text-red-600 mb-3">{oauthError}</p>
      )}

      <div className="flex gap-2">
        <button
          onClick={handleCancel}
          disabled={isSubmitting}
          className={cn(hitlStyles.actionButton, hitlStyles.actionButtonSecondary)}
        >
          Annuler
        </button>
        <button
          onClick={handleConnect}
          disabled={isConnecting || isSubmitting || isWaitingForOAuth}
          className={cn(
            hitlStyles.actionButton,
            'flex-1 text-white',
            config.buttonColor,
            'disabled:opacity-50'
          )}
        >
          {(isConnecting || isSubmitting || isWaitingForOAuth) ? (
            <span className="flex items-center justify-center gap-2">
              <span className={hitlStyles.spinner} />
              {getButtonText()}
            </span>
          ) : (
            `Connecter ${config.name}`
          )}
        </button>
      </div>
    </div>
  );
};
